/**
 * Full-screen list of past Manager-app conversations. Tap one to reopen it in
 * the chat; trash to delete. "New chat" archives the current thread and clears.
 */
import { useState } from 'react';
import { X, Trash2, MessageSquare, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ArchivedChat, loadArchive, deleteArchived } from './chat-history';

function relativeTime(ms?: number): string {
  if (!ms) return '';
  const diff = Date.now() - ms;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ms).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

export default function ChatHistoryPanel({
  uid, onClose, onRestore, onNewChat,
}: {
  uid: string;
  onClose: () => void;
  onRestore: (chat: ArchivedChat) => void;
  onNewChat: () => void;
}) {
  const [list, setList] = useState<ArchivedChat[]>(() => loadArchive(uid));

  const del = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setList(deleteArchived(uid, id));
  };

  return (
    <div className="fixed inset-0 z-[66] flex flex-col bg-background animate-in fade-in duration-150">
      {/* Header */}
      <div className="shrink-0 flex items-center gap-3 border-b border-border bg-card/95 px-4 pb-3 pt-[calc(env(safe-area-inset-top)+1rem)]">
        <button onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground" aria-label="Close">
          <X className="h-4 w-4" />
        </button>
        <h2 className="flex-1 text-base font-semibold text-foreground">Chat history</h2>
        <button
          onClick={onNewChat}
          className="flex items-center gap-1.5 rounded-full bg-[var(--v4-accent)] px-3 py-1.5 text-xs font-medium text-white active:scale-95"
        >
          <Plus className="h-3.5 w-3.5" /> New chat
        </button>
      </div>

      {/* List */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
        {list.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-8 text-center">
            <MessageSquare className="mx-auto h-8 w-8 text-muted-foreground/50" />
            <p className="mt-3 text-sm font-medium text-foreground">No saved conversations yet</p>
            <p className="mt-1 text-xs text-muted-foreground">Start a new chat - when you begin another, this one is saved here.</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {list.map((c) => (
              <button
                key={c.id}
                onClick={() => onRestore(c)}
                className={cn('flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition hover:bg-muted/50')}
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--v4-accent)]/15 text-[var(--v4-accent)]">
                  <MessageSquare className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{c.title}</p>
                  <p className="text-xs text-muted-foreground">{relativeTime(c.savedAt)} - {c.messages?.length || 0} messages</p>
                </div>
                <span
                  onClick={(e) => del(e, c.id)}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-red-500/10 hover:text-red-500"
                  aria-label="Delete conversation"
                  role="button"
                >
                  <Trash2 className="h-4 w-4" />
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
